import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { Field, Form, Formik } from 'formik';

import {
  Button,
  Box,
  Grid,
  Typography,
  FormGroup,
  Checkbox,
  FormControl,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Radio,
  RadioGroup,
  FormHelperText,
  InputAdornment,
  IconButton,
  Stack,
  useTheme,
} from '@mui/material';
import InputField from 'components/Input-Field/input-field';
import { DEFAULT_PERMISSIONS_BY_ROLE, USER_PERMISSIONS } from 'utils/constant';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import FTIcons from 'assets/icons/FTIcons';
import { tokens } from 'theme/tokens';
import FormElements from 'components/FormElements';

const NewUserForm = ({ action, user, isProcessing, toast, saveFormHandler, updateFormHandler }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const steps = ['User Information', 'Roles & Permissions'];
  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const passwordChecklist = [
    { label: 'At least 8 characters', test: (val) => val.length >= 8 },
    { label: 'One uppercase letter', test: (val) => /[A-Z]/.test(val) },
    { label: 'One lowercase letter', test: (val) => /[a-z]/.test(val) },
    { label: 'One number', test: (val) => /[0-9]/.test(val) },
    { label: 'One special character', test: (val) => /[^A-Za-z0-9]/.test(val) },
  ];
  const validationSchema = [
    Yup.object({
      email: Yup.string().email('Please enter user email').max(100).required('Valid email is required.'),
      firstName: Yup.string().max(50).required('Required Field'),
      lastName: Yup.string().max(50).required('Required Field'),
      password: Yup.string().required('Please enter user password')
        .matches(
          /^.*(?=.{8,})((?=.*[!@#$%^&*()\-_=+{};:,<.>]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/,
          'Password must contain at least 8 characters, one uppercase, one number and one special case character'
        ),
      confirmPassword: Yup.string().required('Please confirm user password')
        .when('password', {
          is: password => (password && password.length > 0 ? true : false),
          then: Yup.string().oneOf([Yup.ref('password')], 'Password doesn\'t match')
        }),
    }),
    Yup.object({
      role: Yup.string().required('Please select User Role.'),
      permission: Yup.array().min(1, 'Please assign at-least 1 permission to continue.'),
    }),
  ];

  const getStepContent = (stepIndex) => {
    switch (stepIndex) {
      case 0:
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <FormElements.SectionTitle title='User Information' />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Field name='email'>
                {({ field, meta }) => (
                  <InputField
                    fullWidth
                    label='Enter User Email'
                    variant='outlined'
                    margin='dense'
                    {...field}
                    error={meta.touched && Boolean(meta.error)}
                    helperText={meta.touched && meta.error}
                  />
                )}
              </Field>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Field name='firstName'>
                {({ field, meta }) => (
                  <InputField
                    fullWidth
                    label='Enter user First Name'
                    variant='outlined'
                    margin='dense'
                    {...field}
                    error={meta.touched && Boolean(meta.error)}
                    helperText={meta.touched && meta.error}
                  />
                )}
              </Field>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Field name='lastName'>
                {({ field, meta }) => (
                  <InputField
                    fullWidth
                    label='Enter user Last Name'
                    variant='outlined'
                    margin='dense'
                    {...field}
                    error={meta.touched && Boolean(meta.error)}
                    helperText={meta.touched && meta.error}
                  />
                )}
              </Field>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Field name='password'>
                {({ field, meta }) => (
                  <InputField
                    fullWidth
                    label='Enter user password'
                    variant='outlined'
                    margin='dense'
                    {...field}
                    type={showPassword ? 'text' : 'password'}
                    error={meta.touched && Boolean(meta.error)}
                    helperText={meta.touched && meta.error}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">
                          <IconButton
                            onClick={togglePasswordVisibility}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      }
                    }}
                  />
                )}
              </Field>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Field name='confirmPassword'>
                {({ field, meta }) => (
                  <InputField
                    fullWidth
                    label='Confirm Password'
                    variant='outlined'
                    margin='dense'
                    {...field}
                    type={showPassword ? 'text' : 'password'}
                    error={meta.touched && Boolean(meta.error)}
                    helperText={meta.touched && meta.error}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">
                          <IconButton
                            onClick={togglePasswordVisibility}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      }
                    }}
                  />
                )}
              </Field>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Field>
                {({ form }) => (
                  <Stack direction={'row'} spacing={2}>
                    {passwordChecklist.map(({ label, test }) => (
                      <Box display={'flex'} gap={.5}>
                        <Typography>{test(form.values.password) ?
                          <FTIcons.CheckCircleIcon width={20} height={20} fill={colors.success.main} /> :
                          <FTIcons.CrossCircleIcon width={20} height={20} fill={colors.error.main} />}
                        </Typography>
                        <Typography variant='body1'>{label}</Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Field>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Field name='country'>
                {({ field, meta }) => (
                  <InputField
                    fullWidth
                    label='Enter country'
                    variant='outlined'
                    margin='dense'
                    {...field}
                    error={meta.touched && Boolean(meta.error)}
                    helperText={meta.touched && meta.error}
                  />
                )}
              </Field>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Field name='state'>
                {({ field, meta }) => (
                  <InputField
                    fullWidth
                    label='Enter state'
                    variant='outlined'
                    margin='dense'
                    {...field}
                    error={meta.touched && Boolean(meta.error)}
                    helperText={meta.touched && meta.error}
                  />
                )}
              </Field>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Field name='city'>
                {({ field, meta }) => (
                  <InputField
                    fullWidth
                    label='Enter city'
                    variant='outlined'
                    margin='dense'
                    {...field}
                    error={meta.touched && Boolean(meta.error)}
                    helperText={meta.touched && meta.error}
                  />
                )}
              </Field>
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 8 }} style={{ display: 'flex' }}>
              <Typography variant='subtitle1' sx={{ fontWeight: 600, margin: 'auto 10px' }}>{'Select User Role'}</Typography>
              <Field name='role'>
                {({ field, meta }) => (
                  <FormControl
                    component='fieldset'
                    error={meta.touched && Boolean(meta.error)}
                  >
                    <RadioGroup row {...field}>
                      <FormControlLabel
                        labelPlacement='end'
                        label='DISPATCHER'
                        value='DISPATCHER'
                        control={<Radio size='small' />}
                      />
                      <FormControlLabel
                        labelPlacement='end'
                        label='ACCOUNTANT'
                        value='ACCOUNTANT'
                        control={<Radio size='small' />} />
                      <FormControlLabel
                        labelPlacement='end'
                        label='ADMIN'
                        value='ADMIN'
                        control={<Radio size='small' />} />
                    </RadioGroup>
                    {meta.touched && meta.error && (
                      <FormHelperText>{meta.error}</FormHelperText>
                    )}
                  </FormControl>
                )}
              </Field>
            </Grid>
            <Grid container spacing={1}>
              {USER_PERMISSIONS.map((item) => (
                <Grid size={{ xs: 8 }}>
                  <Box display={'flex'}>
                    <Typography color='caption' style={{ margin: 'auto', fontWeight: 600 }}>{item.component}</Typography>
                    {item.options.map((permission, idx) => (
                      <FormControl required component='fieldset'>
                        <FormGroup row>
                          <Field
                            key={`${permission}-${idx}`}
                            type='checkbox'
                            name='permission'
                            value={permission.value}
                          >
                            {({ field, meta }) => (
                              <FormControlLabel
                                labelPlacement='end'
                                label={permission.label.toUpperCase()}
                                control={<Checkbox size='small' {...field} />}
                              />
                            )}
                          </Field>
                        </FormGroup>
                      </FormControl>
                    ))}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <FormElements.SectionTitle title='Review user information' />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Field>
                {({ form }) => (
                  <>
                    <Typography variant='caption'>{'Email'}</Typography>
                    <Typography variant='subtitle1'>{form.values['email']}</Typography>
                  </>
                )}
              </Field>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Field>
                {({ form }) => (
                  <>
                    <Typography variant='caption'>{'FirstName'}</Typography>
                    <Typography variant='subtitle1'>{form.values['firstName']}</Typography>
                  </>
                )}
              </Field>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Field>
                {({ form }) => (
                  <>
                    <Typography variant='caption'>{'LastName'}</Typography>
                    <Typography variant='subtitle1'>{form.values['lastName']}</Typography>
                  </>
                )}
              </Field>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Field>
                {({ form }) => (
                  <>
                    <Typography variant='caption'>{'Role'}</Typography>
                    <Typography variant='subtitle1'>{form.values['role']}</Typography>
                  </>
                )}
              </Field>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Field>
                {({ form }) => (
                  <>
                    <Typography variant='caption'>{'Permissions'}</Typography>
                    <Typography variant='subtitle1'>{form.values['permissions']}</Typography>
                  </>
                )}
              </Field>
            </Grid>
          </Grid>
        );
    }
  };

  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  return (
    <Grid container spacing={2}>
      <Formik
        initialValues={{ ...user }}
        validationSchema={validationSchema[activeStep]}
        onSubmit={async (values, actions, setFieldValue) => {
          try {
            await validationSchema[activeStep].validate(values, { abortEarly: false });

            if (activeStep === steps.length - 1) {
              // Final step: Review & Submit Form
              // You can post to API here
              const trimmed_data = {};
              Object.keys(values).forEach(key => trimmed_data[key] = typeof (values[key]) === 'string' ? values[key].trim() : values[key]);
              trimmed_data.role = new Array(trimmed_data.role);
              action === 'Save' ? saveFormHandler(trimmed_data) : updateFormHandler(trimmed_data);

            } else {
              if (activeStep === 0) {
                /* 1. Basic Information */
                setActiveStep((prev) => prev + 1);
              } else {
                /* 2. Roles & Permissions */
                setActiveStep((prev) => prev + 1);
              }
            }
            actions.setTouched({});
            actions.setSubmitting(false);
          } catch (err) {
            const errors = {};
            const touched = {};
            err.inner.forEach((e) => {
              errors[e.path] = e.message;
              touched[e.path] = e.message;
            });
            actions.setErrors(errors);
            actions.setTouched(touched);
            actions.setSubmitting(false);
          }
        }}
      >
        {({ values, setFieldValue }) => {
          useEffect(() => {
            if (values.role && DEFAULT_PERMISSIONS_BY_ROLE[values.role]) {
              setFieldValue('permission', DEFAULT_PERMISSIONS_BY_ROLE[values.role]);
            }
          }, [values.role, setFieldValue]);
          return (
            <Form>
              <Stepper activeStep={activeStep} orientation='horizontal'>
                {steps.map((step, index) => {
                  return (
                    <Step key={index}>
                      <StepLabel>{step}</StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
              {isProcessing && <CircularProgress />}
              {getStepContent(activeStep)}
              <Box mt={5}
                display='flex'
                justifyContent='flex-end'
                p={2}
              >
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                >
                  Back
                </Button>
                <Button variant='contained' color='primary' type='submit'>
                  {activeStep === steps.length - 1 ? 'Save User' : 'Next'}
                </Button>
              </Box>
            </Form>
          )
        }}
      </Formik>
    </Grid>
  );
};
export default NewUserForm;